import { lazy, Suspense, useCallback, useState } from 'react'
import type { TagEntry } from '../commands'
import { Btn, Cta, Icon, Keypad, Readout, ScannerFrame, V1Sheet, WaitCta } from './kit'
import { CAMERA_PROBLEM, type CameraProblem } from './cameraProblem'
import { C, typeKey } from './style'
import type { V1IconName } from './sprite'
import { tr } from './text'

const V1Camera = lazy(() => import('./V1Camera'))

/** The red line under a scanner when a code is refused. */
export function Refused({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        borderRadius: 12,
        background: C.badWash,
        border: `2px solid ${C.bad}`,
        color: C.bad,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.3,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 4,
          background: C.bad,
          color: '#ffffff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="x" size={16} sw={3} />
      </span>
      {text}
    </div>
  )
}

/**
 * The live scanner frame; a code read by the camera goes through `check`. When the camera does
 * not open, the frame stays (as on the board) and the reason shows under it, with फिर से कोशिश
 * and — where the screen has no number button of its own — नंबर लिखें.
 */
export function LiveScanner({
  icon,
  hint,
  check,
  onError,
  minHeight,
  onType,
}: {
  icon: V1IconName
  hint: string
  minHeight?: number
  check: (raw: string, entry: TagEntry) => string | null
  onError: (text: string | null) => void
  /** Shown with the camera problem (the seal screen has no number button of its own). */
  onType?: () => void
}) {
  const [problem, setProblem] = useState<CameraProblem | null>(null)
  const [attempt, setAttempt] = useState(0)
  return (
    <>
      <ScannerFrame icon={icon} hint={hint} minHeight={minHeight}>
        {!problem && (
          <Suspense fallback={null}>
            <V1Camera
              key={attempt}
              onDecode={(c) => onError(check(c, 'SCAN'))}
              onUnavailable={setProblem}
            />
          </Suspense>
        )}
      </ScannerFrame>
      {problem && (
        <>
          <Refused text={tr(CAMERA_PROBLEM[problem])} />
          <div style={{ display: 'flex', gap: 8 }}>
            {problem !== 'insecure' && (
              <Btn
                icon="camera"
                onClick={() => {
                  setProblem(null)
                  setAttempt((n) => n + 1)
                }}
              >
                {tr('gate.v1.camera.retry')}
              </Btn>
            )}
            {onType && (
              <Btn icon="keypad" onClick={onType}>
                {tr('gate.v1.tag.type')}
              </Btn>
            )}
          </div>
        </>
      )}
    </>
  )
}

/** "नंबर लिखें": the number printed under the code, on the keypad. */
export function TypeCodeSheet({
  title,
  check,
  onClose,
}: {
  title: string
  check: (raw: string, entry: TagEntry) => string | null
  onClose: () => void
}) {
  const [v, setV] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const onKey = useCallback((k: string) => {
    setErr(null)
    setV((cur) => typeKey(cur, k, { decimals: 0, max: 8 }))
  }, [])
  return (
    <V1Sheet
      title={title}
      onClose={onClose}
      top={120}
      actions={
        v ? (
          <Cta
            label={tr('gate.v1.next')}
            icon="next"
            onClick={() => {
              const e = check(v, 'TYPED')
              if (e) setErr(e)
              else onClose()
            }}
          />
        ) : (
          <WaitCta>{tr('gate.v1.tag.type')}</WaitCta>
        )
      }
    >
      <Readout label={title} value={v} size={32} />
      {err && <Refused text={err} />}
      <div style={{ flexGrow: 1 }} />
      <Keypad dot={false} onKey={onKey} />
    </V1Sheet>
  )
}
